import { useCallback, useEffect, useMemo, useState } from 'react';
import { apiFetch } from '../config/api';

const STUDENT_FIELDS = [
  { key: 'f_name', label: 'First Name' },
  { key: 'l_name', label: 'Last Name' },
  { key: 'dob', label: 'DOB', type: 'date' },
  { key: 'grade', label: 'Grade', type: 'number' },
  { key: 'gender', label: 'Gender', type: 'select', options: ['', 'M', 'F'] },
  { key: 'status', label: 'Status', type: 'select', options: ['active', 'inactive', 'graduate'] },
  { key: 'street_address', label: 'Address' },
  { key: 'city', label: 'City' },
  { key: 'state', label: 'State' },
  { key: 'zip_code', label: 'ZIP' },
];

const CONTACT_FIELDS = [
  { key: 'type', label: 'Type', type: 'select', options: ['parent', 'guardian', 'driver'] },
  { key: 'f_name', label: 'First Name' },
  { key: 'l_name', label: 'Last Name' },
  { key: 'phone', label: 'Phone' },
  { key: 'email', label: 'Email' },
  { key: 'street_address', label: 'Address' },
  { key: 'city', label: 'City' },
  { key: 'state', label: 'State' },
  { key: 'zip_code', label: 'ZIP' },
];
const REVIEW_QUEUE_STATUSES = ['NEEDS_REVIEW', 'FAILED'];

function getPayload(row) {
  return {
    ...(row?.best_payload || {}),
    ...(row?.manual_override_payload || {}),
  };
}

function getIssueText(row) {
  const issues = Array.isArray(row?.validation_issues) ? row.validation_issues : [];
  return issues
    .map((issue) => issue.message || issue.field)
    .filter(Boolean)
    .join('; ');
}

function getRowTitle(row) {
  const payload = getPayload(row);
  const name = [payload.f_name, payload.l_name].filter(Boolean).join(' ').trim();
  if (name) return name;
  return `${row.record_type} row ${row.source_row || row.id}`;
}

function buildInitialDraft(row) {
  return getPayload(row);
}

function buildOverridePayload(row, draft) {
  const best = row.best_payload || {};
  const fields = row.record_type === 'student' ? STUDENT_FIELDS : CONTACT_FIELDS;
  const override = {};

  fields.forEach((field) => {
    const next = draft[field.key] ?? '';
    const original = best[field.key] ?? '';
    if (String(next) !== String(original)) {
      override[field.key] = field.type === 'number' && next !== '' ? Number(next) : next;
    }
  });

  return override;
}

function DetailValue({ label, value }) {
  if (!value) return null;
  return (
    <div className="sir-source-line">
      <span>{label}</span>
      <strong>{value}</strong>
    </div>
  );
}

export default function ImportReviewPage({ token, navigate, schoolId, user, bare = false, SchoolLayoutComponent = null }) {
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  const [activeType, setActiveType] = useState('student');
  const [draft, setDraft] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');

    try {
      const [summaryRes, ...queueResponses] = await Promise.all([
        apiFetch(`/import-review/summary?school_id=${schoolId}`, {}, token),
        ...REVIEW_QUEUE_STATUSES.map((status) => (
          apiFetch(`/import-review?school_id=${schoolId}&status=${status}`, {}, token)
        )),
      ]);
      const nextRows = queueResponses.flatMap((res) => res.data || []);
      setSummary(summaryRes.data || null);
      setRows(nextRows);

      const preferred = nextRows.find((row) => row.record_type === activeType) || nextRows[0] || null;
      setSelectedId((current) => (nextRows.some((row) => row.id === current) ? current : preferred?.id || null));
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [activeType, schoolId, token]);

  useEffect(() => {
    load();
  }, [load]);

  const selectedRow = useMemo(
    () => rows.find((row) => row.id === selectedId) || null,
    [rows, selectedId]
  );

  useEffect(() => {
    if (selectedRow) setDraft(buildInitialDraft(selectedRow));
  }, [selectedRow]);

  const filteredRows = useMemo(
    () => rows.filter((row) => row.record_type === activeType),
    [activeType, rows]
  );

  const counts = useMemo(() => {
    return rows.reduce((acc, row) => {
      acc[row.record_type] = (acc[row.record_type] || 0) + 1;
      return acc;
    }, {});
  }, [rows]);

  const fields = selectedRow?.record_type === 'contact' ? CONTACT_FIELDS : STUDENT_FIELDS;
  const source = selectedRow?.source_payload || {};
  const payload = getPayload(selectedRow);

  const setField = (field) => (event) => {
    setDraft((current) => ({
      ...current,
      [field]: event.target.value,
    }));
  };

  const saveOverride = async () => {
    if (!selectedRow) return null;
    setSaving(true);
    setError('');
    setSuccess('');

    try {
      const manualOverridePayload = buildOverridePayload(selectedRow, draft);
      const res = await apiFetch(`/import-review/${selectedRow.id}`, {
        method: 'PUT',
        body: JSON.stringify({ manual_override_payload: manualOverridePayload }),
      }, token);

      setRows((current) => current.map((row) => (row.id === selectedRow.id ? res.data : row)));
      setSuccess('Corrections saved.');
      return res.data;
    } catch (err) {
      setError(err.message);
      return null;
    } finally {
      setSaving(false);
    }
  };

  const importSelected = async () => {
    if (!selectedRow) return;
    setSaving(true);
    setError('');
    setSuccess('');

    try {
      const manualOverridePayload = buildOverridePayload(selectedRow, draft);
      await apiFetch(`/import-review/${selectedRow.id}/import`, {
        method: 'POST',
        body: JSON.stringify({ manual_override_payload: manualOverridePayload }),
      }, token);

      setSuccess('Row imported.');
      await load();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const content = (
    <div className="sir-page">
      <div className="sir-header">
        <div>
          <h2>Import Review</h2>
          <p>Correct queued records, then import them into the live school roster.</p>
        </div>
        <button className="sa-btn sa-btn-secondary" onClick={() => navigate('students', schoolId)}>
          Back to Students
        </button>
      </div>

      {error ? <div className="sa-error">{error}</div> : null}
      {success ? <div className="sa-success">{success}</div> : null}

      <div className="sir-summary">
        <div>
          <span>Needs Review</span>
          <strong>{summary?.needs_review_total ?? rows.length}</strong>
        </div>
        <div>
          <span>Students</span>
          <strong>{counts.student || 0}</strong>
        </div>
        <div>
          <span>Parents</span>
          <strong>{counts.contact || 0}</strong>
        </div>
      </div>

      {loading ? (
        <div className="sa-empty">Loading review queue...</div>
      ) : rows.length === 0 ? (
        <div className="sa-empty">No queued import records need review.</div>
      ) : (
        <div className="sir-workspace">
          <section className="sir-list-panel">
            <div className="sir-tabs">
              <button className={activeType === 'student' ? 'active' : ''} onClick={() => setActiveType('student')}>
                Students ({counts.student || 0})
              </button>
              <button className={activeType === 'contact' ? 'active' : ''} onClick={() => setActiveType('contact')}>
                Parents ({counts.contact || 0})
              </button>
            </div>

            <div className="sir-row-list">
              {filteredRows.map((row) => (
                <button
                  key={row.id}
                  className={`sir-row-button ${row.id === selectedId ? 'active' : ''}`}
                  onClick={() => setSelectedId(row.id)}
                >
                  <strong>{getRowTitle(row)}</strong>
                  <span>{getIssueText(row) || 'Needs review'}</span>
                  <small>Source row {row.source_row} · Account {row.account_id || 'n/a'}</small>
                </button>
              ))}
            </div>
          </section>

          <section className="sir-editor-panel">
            {selectedRow ? (
              <>
                <div className="sir-editor-heading">
                  <div>
                    <h3>{getRowTitle(selectedRow)}</h3>
                    <p>{getIssueText(selectedRow) || 'Review and import this row.'}</p>
                  </div>
                  <span className="sa-badge sa-badge-trial">{selectedRow.record_type}</span>
                </div>

                <div className="sir-source-grid">
                  <DetailValue label="Lodging" value={source['Lodging Assignment'] || payload.__lodging_assignment} />
                  <DetailValue label="Original DOB" value={source['Birth Date']} />
                  <DetailValue label="Original parent email" value={source['Email Address']} />
                  <DetailValue label="Original phone" value={source['Phone Number']} />
                  <DetailValue label="Address" value={source.Address || payload.street_address} />
                  <DetailValue label="City/ZIP" value={[source.City || payload.city, source.Zip || payload.zip_code].filter(Boolean).join(' ')} />
                </div>

                <div className="sir-form-grid">
                  {fields.map((field) => (
                    <label key={field.key} className="sa-field">
                      <span>{field.label}</span>
                      {field.type === 'select' ? (
                        <select value={draft[field.key] ?? ''} onChange={setField(field.key)}>
                          {field.options.map((option) => (
                            <option key={option} value={option}>{option || 'Select...'}</option>
                          ))}
                        </select>
                      ) : (
                        <input
                          type={field.type || 'text'}
                          value={draft[field.key] ?? ''}
                          onChange={setField(field.key)}
                        />
                      )}
                    </label>
                  ))}
                </div>

                <div className="sir-actions">
                  <button className="sa-btn sa-btn-secondary" disabled={saving} onClick={saveOverride}>
                    Save Corrections
                  </button>
                  <button className="sa-btn sa-btn-primary" disabled={saving} onClick={importSelected}>
                    {saving ? 'Working...' : 'Save & Import'}
                  </button>
                </div>
              </>
            ) : (
              <div className="sa-empty">Select a row to review.</div>
            )}
          </section>
        </div>
      )}
    </div>
  );

  if (bare || !SchoolLayoutComponent) return content;

  return (
    <SchoolLayoutComponent
      token={token}
      navigate={navigate}
      schoolId={schoolId}
      user={user}
      activePage="students"
      breadcrumb="Import Review"
    >
      {content}
    </SchoolLayoutComponent>
  );
}
